import { useCallback, useEffect, useState } from 'react'
import { EXTENSION_ID } from 'services/factoring/constants'

const isChromeRuntimeAvailable = (): boolean => typeof chrome !== 'undefined' && !!chrome.runtime && !!chrome.runtime.sendMessage

/**
 * Lightweight, reliable "is the Numeo Chrome extension installed?" check.
 *
 * Pings the extension via `chrome.runtime.sendMessage` and resolves from `lastError`
 * — the same signal `useExtensionCheck` relies on, but WITHOUT its route gating and
 * install-modal side effects. Use this anywhere you only need a boolean to toggle UI
 * (e.g. hide an "Install Extension" button once it's actually installed).
 *
 * `recheck()` re-pings on demand — call it when the user returns to the tab so the UI
 * updates if they installed the extension in the meantime.
 */
export const useExtensionInstalled = (): { installed: boolean; confirmed: boolean; recheck: () => void } => {
    // Seed from the synchronous chrome.runtime check (what the old FactoringPanel used) so the UI is
    // correct on first paint — otherwise oauth_tab rows are disabled and the "extension required" banner
    // flashes for ~100-500ms until the async PING resolves, even when the extension is installed.
    const [installed, setInstalled] = useState(isChromeRuntimeAvailable)
    // `installed`'s synchronous seed is true in ANY Chrome (chrome.runtime.sendMessage exists without
    // our extension). `confirmed` flips true only after a PING round-trip actually reached the
    // extension — use it when a false positive would suppress a fallback behavior.
    const [confirmed, setConfirmed] = useState(false)

    const recheck = useCallback(() => {
        if (!isChromeRuntimeAvailable()) {
            setInstalled(false)
            setConfirmed(false)
            return
        }
        try {
            chrome.runtime.sendMessage(EXTENSION_ID, { type: 'PING' }, () => {
                // The response can be undefined even when installed — trust lastError only.
                const reachable = !chrome.runtime.lastError
                setInstalled(reachable)
                setConfirmed(reachable)
            })
        } catch {
            setInstalled(false)
            setConfirmed(false)
        }
    }, [])

    useEffect(() => {
        recheck()
    }, [recheck])

    return { installed, confirmed, recheck }
}
